import { css } from '@emotion/react';
import {
  createColor, hexColorWithOpacityToString,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  getDarkOrLightDependingOnBaseColor, getSuitableBorderColor, makeColorDarker, makeColorLighter,
} from '~/_shared/utils/colors/colors.helpers';

const ARROW_HEIGHT = 20;
export const ITEM_SIZE = 26;
const MAX_FONT_SIZE = ITEM_SIZE - 10;
const MIN_FONT_SIZE = 4;
const MAX_OVERLAY_WIDTH = 40;
const BORDER_THICKNESS = 2;

export const getFontColor = (color: string) => {
  const lightTextColor = createColor('#fff');
  const darkTextColor = createColor('#000');
  const itemColor = createColor(color);

  return getDarkOrLightDependingOnBaseColor(itemColor, darkTextColor, lightTextColor).hex;
};

export const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

export const overlappingUIPanelWrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  maxHeight: '100%',
  minHeight: 0,
  marginBlock: 'auto',
});

export const overlappingBackgroundStyle = (theme: Theme) => css({
  backgroundColor: hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.4),
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: 'auto',
  maxHeight: '100%',
  minHeight: 0,
  maxWidth: MAX_OVERLAY_WIDTH,
  width: MAX_OVERLAY_WIDTH,
  minWidth: MAX_OVERLAY_WIDTH,
  padding: '2px 0',
  position: 'relative',
  boxShadow: `0px 0px 4px 2px rgba(0, 0, 0, ${theme.name === 'light' ? 0.4 : 0.6})`,
});

export const overlappingPanelTabsWrapperStyle = css({
  paddingTop: 10,
  position: 'absolute',
  left: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

export const overlappingPanelTabStyle = (theme: Theme) => css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.8),
  borderTopRightRadius: 10,
  borderBottomRightRadius: 10,
  borderBottomWidth: 2,
  borderTopWidth: 2,
  borderRightWidth: 2,
  borderLeftWidth: 0,
  borderStyle: 'solid',
  borderColor: theme.borderColors.primary,
  paddingBlock: 4,
  paddingInline: 8,
  fontSize: 14,
  width: 36,
  height: 28,
  transitionProperty: 'background-color, font-size',
  transitionDuration: '.3s',
  transitionTimingFunction: 'ease-in-out',

  ':focus-visible': {
    borderColor: theme.borderColors.activeItem,
    backgroundColor: theme.backgroundColors.secondaryHover,
  },

  '&[data-is-active="true"]': {
    borderColor: theme.borderColors.activeItem,
    backgroundColor: hexColorWithOpacityToString(theme.backgroundColors.highlight, 0.8),
    fontSize: 20,

    ':hover, :focus-visible': {
      backgroundColor: theme.backgroundColors.highlightHover,
    },
  },

  ':hover': {
    backgroundColor: theme.backgroundColors.secondaryHover,
  },
});

export const contentScrollbarStyle = css({
  transitionProperty: 'height',
  transitionDuration: '.3s',
  transitionTimingFunction: 'ease-in-out',
});

export const contentStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  justifyContent: 'center',
  padding: '6px 0',
});

export const arrowsWrapperStyle = css(centerStyle, {
  flexDirection: 'column',
  padding: '4px 0',
  width: '100%',
});

export const arrowStyle = (disabled?: boolean) => (theme: Theme) => css(centerStyle, {
  color: disabled ? theme.textColors.disabled : theme.textColors.secondary,
  fontSize: 32,
  height: ARROW_HEIGHT,
  width: '100%',
  background: 'transparent',
  border: 'none',

  '&:hover': {
    color: disabled ? theme.textColors.disabled : theme.textColors.info,
  },
});

export const dividerWrapperStyle = css({
  padding: '2px 4px',
  position: 'relative',
});

export const dividerStyle = (theme: Theme) => css({
  backgroundColor: theme.textColors.secondary,
  marginTop: -1,
});

export const getBrighterColor = (color: string) => {
  const itemColor = createColor(color);
  const lighterColor = makeColorLighter(color, 15);
  const darkerColor = makeColorDarker(color, 10);

  return getDarkOrLightDependingOnBaseColor(itemColor, darkerColor, lighterColor);
};

export const itemStyle = (color: string, active: boolean, labelLength: number) => (theme: Theme) => {
  const itemColor = createColor(color);
  const brighterColor = getBrighterColor(color);
  const fontSize = Math.max(MAX_FONT_SIZE - (labelLength - 1) * 2, MIN_FONT_SIZE);

  return css(centerStyle, {
    border: `${BORDER_THICKNESS}px solid ${active ? theme.lineColors.blue : itemColor.hex}`,
    boxSizing: 'border-box',
    fontSize,
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: brighterColor.hex,
      borderColor: active ? theme.lineColors.green : getSuitableBorderColor(brighterColor).hex,
    },
    ...(active && {
      boxShadow: `0 0 0 ${BORDER_THICKNESS}px   ${theme.lineColors.contrastGreen} inset`,
      outline: `${BORDER_THICKNESS}px solid   ${theme.lineColors.contrastGreen}`,
    }),
  });
};

const fadeCommon = (visible: boolean) => css({
  height: `${ARROW_HEIGHT}px`,
  left: 0,
  opacity: visible ? 1 : 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  zIndex: 1,
  transition: 'opacity 0.3s ease',
});

const getGradient = (direction: 'to bottom' | 'to top', theme: Theme) => css({
  background: `linear-gradient(${direction}, ${hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.8)}, transparent)`,
});

export const fadeTopStyle = (visible: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to bottom', theme),
  { top: 0 },
]);

export const fadeBottomStyle = (visible: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to top', theme),
  {
    bottom: 0,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
]);
